// Audio graph setup for DeepgramStreamingEngine — capture + optional resample worklet.
// See docs/planning/deepgram_streaming_robustness.md §3 Phase 3 (older-CPU path).

import { processAndSendPcmFrame } from "./deepgramAudio";
import type { DeepgramDiagnostics } from "./deepgramDiagnostics";
import {
  isPcmCapturePortMessage,
  isPcmResamplePortMessage,
  sendInt16PcmBuffer,
  type DeepgramAudioPath,
  type PcmResampleConfigMessage,
} from "./deepgramPcmPort";

export type DeepgramAudioGraphNodes = {
  sourceNode: MediaStreamAudioSourceNode;
  captureNode: AudioWorkletNode;
  resampleNode: AudioWorkletNode | null;
  audioPath: DeepgramAudioPath;
  inRate: number;
};

export type DeepgramAudioFrameSink = {
  onFrameResult: (result: ReturnType<typeof processAndSendPcmFrame>) => void;
};

const CAPTURE_MODULE_URL = "/asr/pcmCapture.worklet.js";
const RESAMPLE_MODULE_URL = "/asr/pcmResample.worklet.js";

export async function loadDeepgramCaptureWorklet(audioCtx: AudioContext): Promise<void> {
  await audioCtx.audioWorklet.addModule(new URL(CAPTURE_MODULE_URL, import.meta.url));
}

export async function tryLoadDeepgramResampleWorklet(audioCtx: AudioContext): Promise<boolean> {
  try {
    const url = new URL(RESAMPLE_MODULE_URL, import.meta.url);
    // Chromium/Safari 17+ accept `{ type: "module" }` for ES-module worklets.
    await audioCtx.audioWorklet.addModule(
      url,
      { type: "module" } as WorkletOptions,
    );
    return true;
  } catch {
    return false;
  }
}

export function attachDeepgramAudioHandlers(args: {
  nodes: DeepgramAudioGraphNodes;
  getWs: () => WebSocket | null;
  isRunning: () => boolean;
  targetSampleRateHz: number;
  sink: DeepgramAudioFrameSink;
}): void {
  const { nodes, getWs, isRunning, targetSampleRateHz, sink } = args;

  if (nodes.audioPath === "resample-worklet" && nodes.resampleNode) {
    const config: PcmResampleConfigMessage = {
      type: "config",
      targetSampleRateHz,
    };
    nodes.resampleNode.port.postMessage(config);
    nodes.resampleNode.port.onmessage = (ev: MessageEvent<unknown>) => {
      if (!isRunning()) return;
      const ws = getWs();
      if (!ws || !isPcmResamplePortMessage(ev.data)) return;
      sink.onFrameResult(sendInt16PcmBuffer(ws, ev.data.pcm, ev.data.byteLength));
    };
    return;
  }

  nodes.captureNode.port.onmessage = (ev: MessageEvent<unknown>) => {
    if (!isRunning()) return;
    const ws = getWs();
    if (!ws || !isPcmCapturePortMessage(ev.data)) return;
    sink.onFrameResult(
      processAndSendPcmFrame(ws, ev.data.samples, nodes.inRate, undefined, targetSampleRateHz),
    );
  };
}

export async function createDeepgramAudioGraph(args: {
  audioCtx: AudioContext;
  stream: MediaStream;
  targetSampleRateHz: number;
  resampleWorkletAvailable: boolean;
  log?: (...logArgs: unknown[]) => void;
}): Promise<DeepgramAudioGraphNodes> {
  const { audioCtx, stream, targetSampleRateHz, resampleWorkletAvailable, log } = args;
  const sourceNode = audioCtx.createMediaStreamSource(stream);
  const inRate = audioCtx.sampleRate;

  if (resampleWorkletAvailable) {
    try {
      const captureNode = new AudioWorkletNode(audioCtx, "pcm-capture", {
        processorOptions: { passThroughOnly: true },
      });
      const resampleNode = new AudioWorkletNode(audioCtx, "pcm-resample", {
        processorOptions: { targetSampleRateHz },
      });
      sourceNode.connect(captureNode);
      captureNode.connect(resampleNode);
      log?.("[deepgram] audio path: resample-worklet", { inRate, targetSampleRateHz });
      return {
        sourceNode,
        captureNode,
        resampleNode,
        audioPath: "resample-worklet",
        inRate,
      };
    } catch (err) {
      log?.("[deepgram] resample worklet node failed; falling back to main-thread PCM", err);
    }
  }

  const captureNode = new AudioWorkletNode(audioCtx, "pcm-capture");
  sourceNode.connect(captureNode);
  log?.("[deepgram] audio path: main-thread", { inRate, targetSampleRateHz });
  return {
    sourceNode,
    captureNode,
    resampleNode: null,
    audioPath: "main-thread",
    inRate,
  };
}

export function disconnectDeepgramAudioGraph(nodes: DeepgramAudioGraphNodes | null): void {
  if (!nodes) return;
  try {
    nodes.sourceNode.disconnect();
  } catch {
    /* intentional */
  }
  try {
    nodes.captureNode.disconnect();
  } catch {
    /* intentional */
  }
  try {
    nodes.resampleNode?.disconnect();
  } catch {
    /* intentional */
  }
}

export function applyPcmFrameResult(
  diagnostics: DeepgramDiagnostics,
  result: ReturnType<typeof processAndSendPcmFrame>,
  markPcmSent: () => void,
): void {
  if (result.status === "sent") {
    diagnostics.framesSent += 1;
    diagnostics.bytesSent += result.bytesSent;
    markPcmSent();
  } else if (result.status === "dropped") {
    diagnostics.droppedFrames += 1;
  }
}
